import cloneDeep from 'clone-deep'
import { INVALID_ALL_INPUTS } from 'config/api.status.constants'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getAppraisal2PaymentLink,
  getAppraisalComments,
  getAppraisalImportCapableTypes,
  importAppraisalVendorDocuments,
  postAppraisal2Documents,
  postAppraisal2PayOrInvoice,
  postAppraisalComments,
} from 'services'
import { Button, PlainTable } from 'stories/components'
import { formatTime, InputConvert, InputValidate } from 'utils'
import { copyClipboard } from 'utils/copyClipboard'
import { RenderInput } from 'utils/RenderInput'

import { defaultInputs } from '../constants'
import { normalizePaymentLinkEmails } from '../paymentLinkEmails.util'
import { cardFields, IntegrationType, integrationTypeTitles, paymentInformationFormKeys } from '../types'
import { AppraisalVendorModal } from './AppraisalVendorModal'

export const AppraisalNation = ({
  data,
  docTypes,
  onClose,
  integrationType,
}: {
  data: Record<string, any>
  docTypes?: string[]
  onClose: Function
  integrationType: IntegrationType
}) => {
  const [history, setHistory] = useState<any[]>(data.thirdPartyAPI.history || [])
  const [inputs, setInputs] = useState<any>({})
  const [payAction, setPayAction] = useState('')
  const [payLoading, setPayLoading] = useState(false)
  const [importTypes, setImportTypes] = useState<IntegrationType[]>([])
  const [paymentLink, setPaymentLink] = useState<string>(data.thirdPartyAPI?.paymentLink || '')

  const { appraisalID, appraisalFileNumber } = data.thirdPartyAPI
  const typeTitle = integrationTypeTitles[integrationType]
  const isAmcDirect = integrationType === IntegrationType.AMCDirect
  const canSendAmcPaymentLink = isAmcDirect && data.status === 'pendingPayment'

  const visibleInputsLogic = (form: any) => {
    let visibleKeys = ['isSendInvoice', 'paymentLinkEmail']
    if (form['isSendInvoice'].value === false) {
      visibleKeys = ['isSendInvoice', ...cardFields]
    }
    if ([IntegrationType.VisionVMC, IntegrationType.AMCDirect].includes(integrationType)) {
      if (isAmcDirect) {
        visibleKeys = ['paymentLinkEmail']
        form.isSendInvoice.value = true
      } else if (form['isSendInvoice'].value === false) {
        visibleKeys = ['isSendInvoice']
      } else {
        visibleKeys = ['isSendInvoice', 'paymentLinkEmail']
      }
    }
    Object.keys(form).forEach((key) => {
      form[key].visible = visibleKeys.includes(key)
    })
    return form
  }

  useEffect(() => {
    const originInputs = defaultInputs()
    const temp: any = {}
    paymentInformationFormKeys.forEach((key) => {
      if (!['dividerPay', 'paymentAmount'].includes(key)) {
        const input = originInputs[key]
        if (data.info[key] !== undefined) {
          input.value = key === 'paymentLinkEmail' ? normalizePaymentLinkEmails(data.info[key]) : data.info[key]
        }
        temp[key] = input
      }
    })
    if (isAmcDirect) temp.isSendInvoice.value = true
    setInputs(visibleInputsLogic(temp))
    getAppraisalImportCapableTypes().then((res) => {
      if (res?.success) setImportTypes(res.data)
    })
  }, [])

  const onChangeLoanInputFields = (key: string, e: any) => {
    const value = InputConvert(inputs[key], e)
    const error = InputValidate({ ...inputs[key], value })
    setInputs((prev: any) => {
      let temp = cloneDeep(prev)
      temp[key].value = value
      temp[key].error = error
      if (key === 'isSendInvoice') temp = visibleInputsLogic(temp)
      return temp
    })
  }

  const sendPayOrInvoice = async () => {
    let temp = cloneDeep(inputs)
    const json: any = {}
    let hasError = false
    Object.keys(temp).forEach((key) => {
      if (temp[key].visible !== false) {
        const { value } = temp[key]
        const error = InputValidate({ ...temp[key], value })
        temp[key].error = error
        json[key] = value
        if (error.length) hasError = true
      }
    })
    if (isAmcDirect) json.isSendInvoice = true
    setInputs(temp)
    if (hasError) return toast(INVALID_ALL_INPUTS, { type: 'error' })

    setPayLoading(true)
    setPayAction('sendPayOrInvoice')
    const res = await postAppraisal2PayOrInvoice({ id: data.id, info: json })
    if (res.success) {
      setHistory(res.history)
      if (res.paymentLink) setPaymentLink(res.paymentLink)
    }
    setPayLoading(false)
    setPayAction('')
  }

  const renderHistory = () => {
    const rlt = history.map((item: any, index: number) => [index + 1, item.action, item.status, formatTime(item.time)])
    return <PlainTable header={['No', 'Action', 'Status', 'Date']} tdClass="px-4 py-2" data={rlt} />
  }

  const renderPayInvoice = () => {
    if (isAmcDirect && !canSendAmcPaymentLink) {
      return <p className="text-sm text-gray-600">Payment link is available for Pending Payment status only</p>
    }
    return (
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 shadow1 p-4">
          {Object.keys(inputs).map((key, index) => {
            const input = inputs[key]
            if (input.visible === false) return null
            const cn = key === 'isSendInvoice' ? 'md:col-span-2' : 'md-col-span-1'
            return (
              <div className={cn} key={index}>
                <RenderInput input={input} Key={key} onChange={onChangeLoanInputFields} />
              </div>
            )
          })}
          {isAmcDirect && (
            <div className="md:col-span-2">
              <div className="text-sm text-gray-600 mb-1">Payment Link</div>
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  className="flex-1 min-w-[200px] border rounded px-2 py-1 text-sm bg-gray-50"
                  readOnly
                  value={paymentLink}
                  placeholder="Link will appear after AMC Direct provides it"
                />
                <Button link disabled={!paymentLink} onClick={() => paymentLink && copyClipboard(paymentLink)}>
                  Copy Link
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="text-center mt-4">
          <Button
            color="gray"
            onClick={sendPayOrInvoice}
            loading={payAction === 'sendPayOrInvoice'}
            className="min-w-[200px]"
          >
            {isAmcDirect
              ? 'Send Payment Link'
              : inputs['isSendInvoice']?.value === true
              ? 'Send Invoice'
              : [IntegrationType.VisionVMC, IntegrationType.AMCDirect].includes(integrationType)
              ? 'Confirm (No Payment Link)'
              : 'Pay Now'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <AppraisalVendorModal
      title={`${typeTitle} - ${appraisalFileNumber}`}
      data={data}
      onClose={onClose}
      docTypes={docTypes}
      fetchLogs={() => getAppraisalComments(appraisalID, integrationType)}
      reverseOrder
      postComment={(comment) => postAppraisalComments({ appraisalID, comment }, integrationType)}
      postDocuments={(id, documents) => postAppraisal2Documents({ id, documents })}
      menuTabs={['Notification Logs', 'Pay / Invoice', 'Documents']}
      sendMessageLabel={`Send Message to ${typeTitle}`}
      uploadDocumentsLabel={`Upload Documents to ${typeTitle}`}
      renderProgressSection={renderHistory()}
      renderExtraTabContent={(menu) => (menu === 'Pay / Invoice' ? renderPayInvoice() : null)}
      extraLoading={payLoading}
      onLogsLoaded={() => {
        // After comments — a parallel GetBorrowerPaymentLink used to race GetOrderCommentList.
        if (canSendAmcPaymentLink && data.id && !data.thirdPartyAPI?.paymentLink && !paymentLink) {
          getAppraisal2PaymentLink(data.id).then((res) => {
            if (res?.paymentLink) setPaymentLink(res.paymentLink)
          })
        }
      }}
      onImportDocuments={
        importTypes.includes(integrationType) ? () => importAppraisalVendorDocuments(data.id) : undefined
      }
    />
  )
}
